/**
 * /download — Edupra Android app distribution page.
 *
 * The APK is served as a static file from the same nginx host
 * (/edupra.apk → /var/www/edupra-apk/edupra.apk via an nginx location
 * block — see infra/nginx/sites-available/edupra-landing.conf). Pulling
 * down the file fires a Content-Disposition: attachment header so the
 * browser saves rather than navigates.
 *
 * Distribution model: sideload only for now. Schools install the APK
 * directly on their staff + parent phones; we'll move to Play Store
 * once the product clears the public-listing bar.
 */

import { Link } from 'react-router';
import {
  Download,
  ShieldCheck,
  Smartphone,
  Apple,
  ArrowRight,
  CalendarDays,
  GraduationCap,
  MessagesSquare,
  Wallet,
} from 'lucide-react';

const APK_URL = '/edupra.apk';

const FEATURES = [
  {
    icon: CalendarDays,
    title: 'Attendance + leave',
    body: 'Mark daily attendance, raise leave requests, see the class calendar at a glance.',
  },
  {
    icon: GraduationCap,
    title: 'Results + report cards',
    body: 'Term marks land the moment the school publishes — with the printed PDF one tap away.',
  },
  {
    icon: Wallet,
    title: 'Fees in one place',
    body: 'Paid so far, outstanding dues, and online payment when the school enables it.',
  },
  {
    icon: MessagesSquare,
    title: 'Push + inbox',
    body: 'Every announcement reaches you on the lock screen. The inbox holds the audit trail.',
  },
];

export function DownloadPage() {
  return (
    <>
      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <header className="mx-auto max-w-6xl px-6 pb-12 pt-20 md:pt-24">
        <div className="grid items-center gap-12 md:grid-cols-[1.15fr_0.85fr]">
          <div>
            <p
              className="text-[11px] font-semibold uppercase"
              style={{ color: 'var(--color-accent-600)', letterSpacing: '0.10em' }}
            >
              Mobile app
            </p>
            <h1
              className="mt-3"
              style={{
                fontSize: 'clamp(2.25rem, 4.5vw, 3.5rem)',
                lineHeight: 1.04,
                fontWeight: 700,
                letterSpacing: '-0.032em',
              }}
            >
              Edupra in your pocket.
            </h1>
            <p
              className="mt-5 max-w-xl text-[16px] leading-relaxed"
              style={{ color: 'var(--fg-mid)' }}
            >
              The same Edupra workflows staff and parents already trust on the web — now on
              Android. Attendance in three taps, marks the second the school publishes them,
              and a clean inbox for every school message.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href={APK_URL}
                download
                className="inline-flex h-12 items-center gap-2 rounded-md px-5 text-[14px] font-semibold transition-all hover:brightness-110"
                style={{
                  background: 'var(--color-accent-600)',
                  color: '#FAFAFA',
                  boxShadow: '0 8px 24px -8px rgba(16,185,129,0.45)',
                }}
              >
                <Download className="h-4 w-4" strokeWidth={2} />
                Download for Android
              </a>
              <a
                href="#install"
                className="inline-flex h-12 items-center gap-2 rounded-md border px-4 text-[13px] font-medium"
                style={{
                  borderColor: 'var(--border-line)',
                  color: 'var(--fg-mid)',
                }}
              >
                How to install
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
              </a>
            </div>

            <div
              className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px]"
              style={{ color: 'var(--fg-low)' }}
            >
              <span className="inline-flex items-center gap-1.5">
                <Smartphone className="h-3.5 w-3.5" strokeWidth={1.75} />
                Android 9 (Pie) and later
              </span>
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.75} />
                Signed by Edupra
              </span>
            </div>
          </div>

          {/* Phone-frame illustration */}
          <PhoneFrame />
        </div>
      </header>

      {/* ── What's inside ──────────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-6 py-14">
        <div className="mb-8">
          <p
            className="text-[11px] font-semibold uppercase"
            style={{ color: 'var(--color-accent-600)', letterSpacing: '0.10em' }}
          >
            What you get
          </p>
          <h2
            className="mt-2 text-[28px] font-bold leading-tight tracking-tight md:text-[34px]"
            style={{ letterSpacing: '-0.02em' }}
          >
            Built for the home screen, not the inbox.
          </h2>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="rounded-2xl border p-6"
              style={{
                background: 'var(--bg-surface)',
                borderColor: 'var(--border-line)',
              }}
            >
              <div
                className="flex h-10 w-10 items-center justify-center rounded-lg"
                style={{ background: 'var(--bg-sunken)' }}
              >
                <f.icon className="h-5 w-5" style={{ color: 'var(--fg-mid)' }} />
              </div>
              <h3 className="mt-4 text-[16px] font-semibold" style={{ color: 'var(--fg-high)' }}>
                {f.title}
              </h3>
              <p className="mt-2 text-[14px] leading-relaxed" style={{ color: 'var(--fg-mid)' }}>
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Install instructions ───────────────────────────────────────── */}
      <section
        id="install"
        className="mx-auto max-w-6xl px-6 py-14 scroll-mt-24"
      >
        <div className="mb-8">
          <p
            className="text-[11px] font-semibold uppercase"
            style={{ color: 'var(--color-accent-600)', letterSpacing: '0.10em' }}
          >
            Install in one minute
          </p>
          <h2
            className="mt-2 text-[28px] font-bold leading-tight tracking-tight md:text-[34px]"
            style={{ letterSpacing: '-0.02em' }}
          >
            Sideload, sign in, done.
          </h2>
        </div>

        <ol className="space-y-3">
          <Step n={1}>
            Tap <strong>Download for Android</strong> above to save the APK to your phone.
          </Step>
          <Step n={2}>
            Open the file from your <em>Downloads</em> folder. Android will ask permission to install
            from this source — say yes once. You can revoke that permission afterward in Settings →
            Apps → <em>Special app access</em> → <em>Install unknown apps</em>.
          </Step>
          <Step n={3}>
            Launch <strong>Edupra</strong> and sign in with the same email or phone number your school
            registered. The app picks up your role automatically — staff get attendance and timetable,
            parents get results and fees.
          </Step>
          <Step n={4}>
            Allow notifications when prompted. We use them sparingly — published results, attendance
            alerts, and announcements your school office sends.
          </Step>
        </ol>

        <p
          className="mt-6 text-[12.5px]"
          style={{ color: 'var(--fg-low)' }}
        >
          Need help? Email us at{' '}
          <a
            href="mailto:hello@edupra.com"
            className="font-medium hover:underline"
            style={{ color: 'var(--color-accent-600)' }}
          >
            hello@edupra.com
          </a>
          .
        </p>
      </section>

      {/* ── iOS ──────────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-6 pb-24 pt-6">
        <div
          className="flex flex-col items-start gap-4 rounded-2xl border p-6 md:flex-row md:items-center md:justify-between md:p-8"
          style={{
            borderColor: 'var(--border-line)',
            background: 'var(--bg-surface)',
          }}
        >
          <div className="flex items-center gap-4">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-xl"
              style={{ background: 'var(--bg-sunken)' }}
            >
              <Apple className="h-6 w-6" style={{ color: 'var(--fg-mid)' }} />
            </div>
            <div>
              <p
                className="text-[11px] font-semibold uppercase"
                style={{ color: 'var(--fg-low)', letterSpacing: '0.10em' }}
              >
                iOS · coming soon
              </p>
              <h3 className="text-[18px] font-semibold" style={{ color: 'var(--fg-high)' }}>
                On the way to the App Store.
              </h3>
            </div>
          </div>
          <p className="text-[13.5px] leading-relaxed md:max-w-md md:text-right" style={{ color: 'var(--fg-mid)' }}>
            We're submitting Edupra for iOS for review next. Parents on iPhone can use the web app at{' '}
            <Link
              to="/"
              className="font-medium hover:underline"
              style={{ color: 'var(--color-accent-600)' }}
            >
              edupra.com
            </Link>{' '}
            in the meantime — it covers every screen the mobile app does.
          </p>
        </div>
      </section>
    </>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li
      className="flex gap-4 rounded-lg border p-4"
      style={{
        borderColor: 'var(--border-line)',
        background: 'var(--bg-surface)',
      }}
    >
      <span
        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-bold"
        style={{
          background: 'var(--color-accent-600)',
          color: '#FAFAFA',
        }}
      >
        {n}
      </span>
      <span
        className="pt-1 text-[14px] leading-relaxed"
        style={{ color: 'var(--fg-high)' }}
      >
        {children}
      </span>
    </li>
  );
}

function PhoneFrame() {
  return (
    <div className="flex items-center justify-center">
      <div
        className="relative h-[420px] w-[210px] rounded-[36px] border-[10px] p-2 shadow-2xl"
        style={{
          borderColor: '#08080B',
          background: '#08080B',
        }}
      >
        {/* Inner screen */}
        <div
          className="relative h-full w-full overflow-hidden rounded-[26px]"
          style={{
            background:
              'radial-gradient(60% 80% at 50% 0%, rgba(16,185,129,0.22) 0%, transparent 60%), #FAFAFA',
          }}
        >
          {/* Notch */}
          <div
            className="absolute left-1/2 top-2 z-10 h-4 w-16 -translate-x-1/2 rounded-full"
            style={{ background: '#08080B' }}
          />
          {/* App body mock */}
          <div className="absolute inset-0 flex flex-col p-5 pt-10">
            <div className="text-[9px] font-semibold tracking-wide" style={{ color: '#059669' }}>
              EDUPRA
            </div>
            <div className="mt-1 text-[14px] font-bold" style={{ color: '#08080B' }}>
              Hello, Babishram
            </div>
            <div className="mt-0.5 text-[9px]" style={{ color: '#475569' }}>
              SR School of Excellence
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2">
              <MockCard label="Today" value="9:30" sub="1st period" />
              <MockCard label="Attendance" value="94%" sub="YTD" />
              <MockCard label="Results" value="2" sub="new" highlight />
              <MockCard label="Fees" value="₹0" sub="due" />
            </div>

            <div
              className="mt-4 rounded-lg p-3"
              style={{ background: '#F1F5F9', border: '1px solid #E2E8F0' }}
            >
              <div className="text-[8px] font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>
                Just in
              </div>
              <div className="mt-1 text-[10px] font-medium" style={{ color: '#08080B' }}>
                II MID TERM results published
              </div>
              <div className="text-[8.5px]" style={{ color: '#64748B' }}>
                Tap to see Hivasthika's report card
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MockCard({
  label,
  value,
  sub,
  highlight = false,
}: {
  label: string;
  value: string;
  sub: string;
  highlight?: boolean;
}) {
  return (
    <div
      className="rounded-lg p-2"
      style={{
        background: highlight ? '#ECFDF5' : '#FFFFFF',
        border: `1px solid ${highlight ? '#A7F3D0' : '#E2E8F0'}`,
      }}
    >
      <div
        className="text-[7.5px] font-semibold uppercase tracking-wider"
        style={{ color: highlight ? '#059669' : '#94A3B8' }}
      >
        {label}
      </div>
      <div className="text-[12px] font-bold" style={{ color: '#08080B' }}>
        {value}
      </div>
      <div className="text-[7.5px]" style={{ color: '#94A3B8' }}>
        {sub}
      </div>
    </div>
  );
}
